/**
 * Everything the three Trade Hub panes read and write, resolved once.
 *
 * WHY IT IS A HOOK ABOVE THE PANES. The panes share almost all of their data —
 * the trade list feeds Potential's centrepiece AND Proposed's Trade Match
 * highlight; the proposal actions move rows between Proposed and Completed. If
 * each pane owned its own queries, swiping would refetch and the two proposal
 * lists could disagree with each other for a beat. One owner, one cache, and
 * swiping is free.
 *
 * The filter state lives here too, so the pinned filter rows survive a swipe
 * away and back. Compare deliberately keeps its OWN copy of this state — the
 * two surfaces answer different questions and should be able to sit on
 * different filters at the same time.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { useAuth } from "@/components/AuthProvider";
import { hasProAccess, useSession } from "@/components/SessionProvider";
import { useToast } from "@/hooks/use-toast";
import { speciesName } from "@/data/speciesNames";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { markTradeCompletion } from "@/lib/tradeCompletion";
import { markSharedAsCard, type TradePairing } from "@/lib/tradeShareLog";
import { renderShareCard } from "@/lib/shareCard";
import { shareImageBlob } from "@/lib/shareImage";
import { getDexEntryState } from "@/lib/dexStateStore";
import { isBaseStage } from "@/lib/generated/baseStageSpecies.generated";
import { isBabyEvolution } from "@/lib/evolutionFamily";
import { isLegendaryDex } from "@/lib/legendaryDexNumbers";
import { bothShinyAndLuckyIds, luckyLeadComparator } from "@/lib/luckyFriendRanking";
import { tradableLeads } from "@/lib/compareLeads";
import { copyToClipboard } from "@/lib/copyToClipboard";
import { buildTradeSearchString } from "@/lib/tradeSearchString";
import { applyCompletedTradesToDex } from "@/lib/tradeDexLift";
import type { PokeType } from "@/lib/types";
import type { SpeciesClass } from "@/lib/speciesClass";
import type { RegionFilter, SortOrder } from "@/lib/dexGridFilter";
import {
  DEFAULT_TRADE_LEAD_FILTERS,
  matchesTradeLead,
  matchesTradeLeadForm,
  tradeLeadActiveAxes,
  tradeLeadComparator,
  type TradeLeadFilters,
} from "@/lib/tradeLeadFilter";
import { parseCompareFilters, queryStringFromLocation } from "@/lib/compareFilters";
import { trainerDisplayName } from "@/lib/trainerIdentity";
import { useFriendIdentity } from "@/lib/useFriendIdentity";
import { MAX_WISHLIST, useWishlist, type WishlistEntry } from "@/lib/wishlistStore";
import { type TradeProposal } from "@/components/TradeProposalsCard";
import type { MyPokemonRecord } from "@/lib/myPokemonStore";
import { recordCompareVariant } from "@/lib/recordCompareVariant";
import {
  proposedSideKeys,
  selectTradeMatchHighlight,
  tradeSideKey,
  type TradeHighlight,
  type TradeSide,
} from "@/lib/tradeMatchModel";
import {
  ALLOWED_VARIANTS,
  normVariant,
  REGION_FILTERS,
  VARIANT_ORDER,
  type CompareVariant,
  type LiveCompare,
  type LiveCompareRow,
  type PanelRow,
  type PlannedTrade,
  type TradeDirection,
} from "@/components/trade/compareTypes";

export type TradeHubData = ReturnType<typeof useTradeHubData>;

export function useTradeHubData(friendId: number | null) {
  const { account } = useAuth();
  const { tier, openPaywall } = useSession();
  const { toast } = useToast();
  const isPro = hasProAccess(tier);

  const { friend, isLoading: friendLoading } = useFriendIdentity(friendId);
  const friendName = friend?.displayName ?? "Trainer";

  /* ── filters (this page's own; Compare keeps a separate copy) ───────────── */

  // The LEAD filters: the Dex page's filter system applied to a trade lead
  // (2026-09-09 ask). Region used to be a pill row of this page's own; it is
  // now the same Region section the Dex menu has, beside Type, Class, search
  // and sort, all from lib/tradeLeadFilter. The variant pills stay separate:
  // they are the dex axis, not a species filter.
  const [region, setRegion] = useState<RegionFilter>(DEFAULT_TRADE_LEAD_FILTERS.region);
  const [typeFilter, setTypeFilter] = useState<PokeType[]>([]);
  const [classFilter, setClassFilter] = useState<SpeciesClass[]>([]);
  const [leadSearch, setLeadSearch] = useState("");
  const [leadSort, setLeadSort] = useState<SortOrder>(DEFAULT_TRADE_LEAD_FILTERS.sort);
  // Form-kind switches (2026-09-19): hide costume / regional form leads.
  const [hideCostume, setHideCostume] = useState(DEFAULT_TRADE_LEAD_FILTERS.hideCostume);
  const [hideRegional, setHideRegional] = useState(DEFAULT_TRADE_LEAD_FILTERS.hideRegional);
  const toggleType = (t: PokeType) =>
    setTypeFilter((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  const toggleClass = (c: SpeciesClass) =>
    setClassFilter((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  const resetLeadFilters = () => {
    setRegion(DEFAULT_TRADE_LEAD_FILTERS.region);
    setTypeFilter([]);
    setClassFilter([]);
    setLeadSearch("");
    setLeadSort(DEFAULT_TRADE_LEAD_FILTERS.sort);
    setHideCostume(DEFAULT_TRADE_LEAD_FILTERS.hideCostume);
    setHideRegional(DEFAULT_TRADE_LEAD_FILTERS.hideRegional);
  };
  const leadFilters = useMemo<TradeLeadFilters>(
    () => ({
      region, types: typeFilter, classes: classFilter, search: leadSearch, sort: leadSort,
      hideCostume, hideRegional,
    }),
    [region, typeFilter, classFilter, leadSearch, leadSort, hideCostume, hideRegional],
  );
  const leadActiveAxes = useMemo(() => tradeLeadActiveAxes(leadFilters), [leadFilters]);
  const leadActiveCount = useMemo(
    () => Object.values(leadActiveAxes).filter(Boolean).length,
    [leadActiveAxes],
  );
  const leadFiltersActive = leadActiveCount > 0;

  const [variantFilter, setVariantFilter] = useState<CompareVariant | "all">("all");
  const [baseOnly, setBaseOnly] = useState(false);
  const [luckyFriend, setLuckyFriend] = useState(false);
  const [showAllYours, setShowAllYours] = useState(false);
  const [showAllTheirs, setShowAllTheirs] = useState(false);
  const [showAllTradeList, setShowAllTradeList] = useState(false);

  // Deep-link support: ?category=shiny&region=kanto pre-applies both filters,
  // the same contract Compare honours so old links keep working when they land
  // here. `category=main` maps to the "All" variant pill.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const search = queryStringFromLocation(window.location.hash, window.location.search);
    const isValidRegion = (v: string) => REGION_FILTERS.some((r) => r.key === v);
    const parsed = parseCompareFilters(search, isValidRegion);
    if (parsed.category) {
      setVariantFilter(
        parsed.category === "main"
          ? "all"
          : (VARIANT_ORDER as string[]).includes(parsed.category)
            ? (parsed.category as CompareVariant)
            : "all",
      );
    }
    if (parsed.region) setRegion(parsed.region as RegionFilter);
  }, []);

  /* ── queries ────────────────────────────────────────────────────────────── */

  // One full fetch (every variant, all regions); the pill filters narrow it
  // client-side, so switching a filter is instant and never refetches.
  const compareUrl = friendId != null ? `/api/friends/${friendId}/compare` : null;
  const tradeListUrl = friendId != null ? `/api/friends/${friendId}/trade-list` : null;
  const wishlistUrl = friendId != null ? `/api/friends/${friendId}/wishlist` : null;
  // The two proposal reads are deliberately SEPARATE queries against the same
  // endpoint. Live proposals change on every action and want a short staleTime
  // plus polling (the other side's agree has to show up); history only grows,
  // is fetched at a much larger limit, and should not be refetched every time
  // someone taps Agree.
  const liveProposalsUrl =
    friendId != null ? `/api/friends/${friendId}/trade-proposals?status=pending,agreed` : null;
  const historyUrl =
    friendId != null ? `/api/friends/${friendId}/trade-proposals?status=completed&limit=200` : null;

  const compareQ = useQuery<LiveCompare>({
    queryKey: compareUrl ? [compareUrl] : ["hub-compare-off"],
    enabled: Boolean(compareUrl),
    staleTime: 30_000,
  });
  const tradeListQ = useQuery<{ trades: PlannedTrade[] }>({
    queryKey: tradeListUrl ? [tradeListUrl] : ["hub-trade-list-off"],
    enabled: Boolean(tradeListUrl),
    staleTime: 15_000,
  });
  const liveProposalsQ = useQuery<{ proposals: TradeProposal[] }>({
    queryKey: liveProposalsUrl ? [liveProposalsUrl] : ["hub-live-proposals-off"],
    enabled: Boolean(liveProposalsUrl),
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
  const historyQ = useQuery<{ proposals: TradeProposal[] }>({
    queryKey: historyUrl ? [historyUrl] : ["hub-history-off"],
    enabled: Boolean(historyUrl),
    staleTime: 60_000,
  });
  type WishlistWireEntry = { variant: string; dexNumber: number; formId?: string | null; addedAt?: number };
  const friendWishlistQ = useQuery<{ entries: WishlistWireEntry[]; updatedAt: string | null }>({
    queryKey: wishlistUrl ? [wishlistUrl] : ["hub-wishlist-off"],
    enabled: Boolean(wishlistUrl),
    staleTime: 30_000,
  });

  const trades = tradeListQ.data?.trades ?? [];

  /**
   * The `?status=` scoping is a REQUEST, not a guarantee — so both lists are
   * filtered again here.
   *
   * Seen on device 2026-08-12: one completed trade appeared in Proposed AND in
   * Completed at the same time, rendering as "Trade complete" on the pane whose
   * entire job is things that are still in flight. The status parameter is
   * newer than the deployed worker, and a worker that does not know it ignores
   * it and returns every status mixed together — which is exactly what the
   * old, pre-hub endpoint always did.
   *
   * A stale worker is a normal condition, not an incident: the app updates
   * through the store on its own schedule. So the client states what each layer
   * IS rather than trusting the query it asked for. Cheap, and it keeps the two
   * panes disjoint no matter what comes back.
   */
  const liveProposals = (liveProposalsQ.data?.proposals ?? []).filter(
    (p) => p.status === "pending" || p.status === "agreed",
  );
  const history = (historyQ.data?.proposals ?? []).filter((p) => p.status === "completed");
  const myWishlist = useWishlist();
  const friendWishlist: WishlistEntry[] = (friendWishlistQ.data?.entries ?? [])
    .filter((e) => e && typeof e.variant === "string" && typeof e.dexNumber === "number")
    .slice(0, MAX_WISHLIST)
    .map((e) => ({
      variant: e.variant as WishlistEntry["variant"],
      dexNumber: e.dexNumber,
      ...(e.formId ? { formId: e.formId } : {}),
      addedAt: typeof e.addedAt === "number" ? e.addedAt : 0,
    }));

  /* ── trade-list mutations (optimistic, so add/hide/remove feel instant) ─── */

  const patchTrades = (updater: (prev: PlannedTrade[]) => PlannedTrade[]): PlannedTrade[] => {
    if (!tradeListUrl) return [];
    const prev = queryClient.getQueryData<{ trades: PlannedTrade[] }>([tradeListUrl])?.trades ?? [];
    queryClient.setQueryData<{ trades: PlannedTrade[] }>([tradeListUrl], { trades: updater(prev) });
    return prev;
  };
  const rollbackTrades = (prev: PlannedTrade[]) => {
    if (tradeListUrl) queryClient.setQueryData<{ trades: PlannedTrade[] }>([tradeListUrl], { trades: prev });
  };
  const refetchTrades = () => {
    if (tradeListUrl) void queryClient.invalidateQueries({ queryKey: [tradeListUrl] });
  };
  /** An action in one proposal layer can move a row into the other, so both refresh. */
  const refetchProposals = () => {
    if (liveProposalsUrl) void queryClient.invalidateQueries({ queryKey: [liveProposalsUrl] });
    if (historyUrl) void queryClient.invalidateQueries({ queryKey: [historyUrl] });
  };

  const addTradeMut = useMutation<
    unknown,
    Error,
    { speciesId: number; variant: CompareVariant; direction: TradeDirection; formId?: string | null },
    { prev: PlannedTrade[] }
  >({
    mutationFn: (v) => apiRequest("POST", `${tradeListUrl}`, v).then((r) => r.json()),
    onMutate: (v) => {
      const prev = patchTrades((rows) => {
        const key = `${v.direction}:${v.variant}:${v.speciesId}`;
        if (rows.some((t) => `${t.direction}:${t.variant}:${t.speciesId}` === key)) {
          return rows.map((t) =>
            `${t.direction}:${t.variant}:${t.speciesId}` === key ? { ...t, status: "planned" } : t,
          );
        }
        return [
          ...rows,
          { id: -Date.now(), speciesId: v.speciesId, variant: v.variant, direction: v.direction, formId: v.formId ?? null, status: "planned", createdAt: Date.now() },
        ];
      });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx) rollbackTrades(ctx.prev);
      toast({ title: "Couldn't add to trade list", description: "Please try again.", variant: "destructive" });
    },
    onSettled: refetchTrades,
  });

  const hideTradeMut = useMutation<unknown, Error, PlannedTrade, { prev: PlannedTrade[] }>({
    mutationFn: (t) =>
      apiRequest("POST", `${tradeListUrl}/${t.id}/${t.status === "hidden" ? "unhide" : "hide"}`, {}).then((r) => r.json()),
    onMutate: (t) => {
      const next: PlannedTrade["status"] = t.status === "hidden" ? "planned" : "hidden";
      const prev = patchTrades((rows) => rows.map((r) => (r.id === t.id ? { ...r, status: next } : r)));
      return { prev };
    },
    onError: (_e, _t, ctx) => { if (ctx) rollbackTrades(ctx.prev); },
    onSettled: refetchTrades,
  });

  const removeTradeMut = useMutation<unknown, Error, number, { prev: PlannedTrade[] }>({
    mutationFn: (id) => apiRequest("DELETE", `${tradeListUrl}/${id}`, {}).then((r) => r.json()),
    onMutate: (id) => {
      const prev = patchTrades((rows) => rows.filter((r) => r.id !== id));
      return { prev };
    },
    onError: (_e, _id, ctx) => { if (ctx) rollbackTrades(ctx.prev); },
    onSettled: refetchTrades,
  });

  /* ── proposal transitions ────────────────────────────────────────────────── */

  const [proposalBusyId, setProposalBusyId] = useState<number | null>(null);

  const proposalActionMut = useMutation<unknown, Error, { id: number; action: "agree" | "decline" | "cancel" }>({
    mutationFn: ({ id, action }) =>
      apiRequest("POST", `/api/trade-proposals/${id}/${action}`, {}).then((r) => r.json()),
    onMutate: ({ id }) => setProposalBusyId(id),
    onSuccess: (_data, { action }) => {
      // Agreeing completes the trade outright now, so it is the moment to say
      // so — and the dex lift for the species I just took follows from the
      // completed row via applyCompletedTradesToDex below, same as it does for
      // the trainer who wasn't holding their phone.
      if (action === "agree") {
        toast({
          title: "Trade agreed, and logged",
          description: "Make the swap in-game. It's already in your trade history.",
        });
      }
    },
    onError: () =>
      toast({ title: "Couldn't update the trade", description: "Please try again.", variant: "destructive" }),
    onSettled: () => { setProposalBusyId(null); refetchProposals(); },
  });

  const completeProposalMut = useMutation<unknown, Error, TradeProposal>({
    mutationFn: (p) => apiRequest("POST", `/api/trade-proposals/${p.id}/complete`, {}).then((r) => r.json()),
    onMutate: (p) => setProposalBusyId(p.id),
    onSuccess: (_data, p) => {
      // Update MY OWN Dex for this swap — device-authoritative; the friend's
      // device does the same on their side. markTradeCompletion marks both the
      // species I gave and the one I received as caught.
      const iAmProposer = p.fromAccountId === account?.id;
      const outgoingId = iAmProposer ? p.giveSpeciesId : p.getSpeciesId;
      const incomingId = iAmProposer ? p.getSpeciesId : p.giveSpeciesId;
      markTradeCompletion(
        {
          outgoingSpeciesId: outgoingId,
          incomingSpeciesId: incomingId,
          outgoingSpeciesName: speciesName(outgoingId),
          incomingSpeciesName: speciesName(incomingId),
        },
        { toast },
      );
    },
    onError: () =>
      toast({ title: "Couldn't complete the trade", description: "Please try again.", variant: "destructive" }),
    onSettled: () => { setProposalBusyId(null); refetchProposals(); },
  });

  /**
   * Apply finished trades to MY dex.
   *
   * Agreement completes a proposal for both trainers, so half the time the
   * trade finishes while this device is asleep. There is no push to catch — the
   * lift is applied whenever this client next reads the completed list, guarded
   * by a local ledger so a trade is only ever applied once per device (see
   * tradeDexLift). One summary toast for the batch, not one per trade: a
   * trainer opening the app after a week away should not be handed a stack of
   * notifications about swaps they already know they made.
   */
  useEffect(() => {
    if (history.length === 0) return;
    const { newSpeciesIds, appliedIds } = applyCompletedTradesToDex(history, account?.id ?? null);
    if (appliedIds.length === 0) return;
    if (newSpeciesIds.length === 0) return; // Applied, but nothing was new to the dex.
    toast({
      title: newSpeciesIds.length === 1 ? "Trade added to your Dex" : `${newSpeciesIds.length} trades added to your Dex`,
      description:
        newSpeciesIds.length === 1
          ? `${speciesName(newSpeciesIds[0])} is now marked caught.`
          : `${newSpeciesIds.map(speciesName).slice(0, 3).join(", ")}${newSpeciesIds.length > 3 ? "…" : ""} now marked caught.`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyQ.data, account?.id]);

  /* ── the comparison model ───────────────────────────────────────────────── */

  const compare = useMemo(() => {
    const live = compareQ.data;
    if (!live) {
      // Numeric id but the request is still in flight or failed. A safe empty
      // shape, never demo content.
      return {
        youCaughtCount: 0,
        theirCaughtCount: 0,
        yourPanel: [] as PanelRow[],
        theirPanel: [] as PanelRow[],
        yourPanelTotal: 0,
        theirPanelTotal: 0,
        locked: false,
        loaded: false,
      };
    }
    // For Basic the arrays are the server's truncated preview; the true counts
    // come from the *Total fields so stats and teases stay correct.
    const iHaveTotal = live.iHaveTotal ?? live.iHave.length;
    const theyHaveTotal = live.theyHaveTotal ?? live.theyHave.length;
    // Every lead tile carries an add-to-trade-list action, so a species Pokémon
    // GO refuses to trade is not a lead — dropped here as well as server-side,
    // so an app running against an older worker deploy still can't offer a dead
    // trade. See compareLeads.ts, which also restates the Basic preview totals.
    const { rows: iHaveRows, total: iHaveLeadTotal } = tradableLeads(live.iHave, iHaveTotal);
    const { rows: theyHaveRows, total: theyHaveLeadTotal } = tradableLeads(live.theyHave, theyHaveTotal);
    // formId rides along: the server folds a base-form row onto the base row, so
    // anything still carrying one is a genuinely distinct copy and the tile has
    // to say which. A variant this surface has no tile for (shinyLucky, shundo)
    // is dropped, never re-labelled as Main.
    const toPanelRow = (r: LiveCompareRow): PanelRow | null => {
      const variant = normVariant(r.variant);
      if (variant == null) return null;
      return {
        id: r.speciesId,
        variant,
        isShiny: r.variant === "shiny",
        formId: r.formId ?? null,
        reference: r.reference === true,
      };
    };
    const panelRows = (rows: LiveCompareRow[]): PanelRow[] =>
      rows.map(toPanelRow).filter((r): r is PanelRow => r != null);
    return {
      // Caught counts stay on the UNFILTERED totals: an untradable species you
      // own is still one you've caught, it just isn't a lead.
      youCaughtCount: live.bothHave.length + iHaveTotal,
      theirCaughtCount: live.bothHave.length + theyHaveTotal,
      yourPanel: panelRows(iHaveRows),
      theirPanel: panelRows(theyHaveRows),
      yourPanelTotal: iHaveLeadTotal,
      theirPanelTotal: theyHaveLeadTotal,
      locked: live.locked ?? false,
      loaded: true,
    };
  }, [compareQ.data]);

  // Keys already on the trade list, so a surfaced lead isn't shown twice.
  const plannedKeys = useMemo(() => {
    const s = new Set<string>();
    for (const t of trades) s.add(`${t.direction}:${t.variant}:${t.speciesId}`);
    return s;
  }, [trades]);

  const wishlistedIds = useMemo(() => {
    const ids = new Set<number>();
    for (const w of myWishlist) ids.add(w.dexNumber);
    for (const w of friendWishlist) ids.add(w.dexNumber);
    return ids;
  }, [myWishlist, friendWishlistQ.data]);

  /**
   * Variant + lead filters (region / type / class / search) + base-stage, all
   * client-side over the full set, then the chosen sort.
   *
   * Lucky-friend mode PRIORITISES Lucky & Shiny (see the ranking below) but is
   * NOT exclusive to them: a lucky friend makes every trade come back Lucky, so
   * a trainer may well want a plain Lucky of something else, or any other
   * variant. The dex-variant filter therefore stays authoritative in both modes,
   * and the pill row stays on screen so the control is never a dead end.
   */
  const { yourFiltered, theirFiltered } = useMemo(() => {
    const inShinyLuckyDex = (id: number) => getDexEntryState("shinyLucky", id)?.state === "present";
    const matchesFilters = (r: PanelRow) =>
      ALLOWED_VARIANTS.has(r.variant) &&
      (variantFilter === "all" || r.variant === variantFilter) &&
      // A Lucky REFERENCE row (the friend holds the species, a lucky trade
      // could make it Lucky) is a fact about the Lucky dex, so it shows under
      // that pill only. Under "All" the friend's whole collection was
      // arriving as Lucky leads and burying the real ones (2026-09-19).
      (variantFilter === "lucky" || !r.reference) &&
      matchesTradeLead(r.id, leadFilters) &&
      matchesTradeLeadForm(r.id, r.formId, leadFilters) &&
      // A baby's first evolution counts as base (Pikachu beside Pichu), the
      // same rule the Dex page's Base filter uses. See isBaseOrBabyEvolution.
      (!baseOnly || isBaseStage(r.id) || isBabyEvolution(r.id)) &&
      // A species already in the Shiny-Lucky dex adds nothing on the Lucky/Shiny
      // axes while lucky-friend mode is on — but that must never suppress the
      // OTHER variants, which the Shiny-Lucky dex says nothing about.
      (!luckyFriend
        || (r.variant !== "lucky" && r.variant !== "shiny")
        || !inShinyLuckyDex(r.id));

    // A lead already on the trade list is not surfaced again — it lives in the
    // trade-list block at the top of the Potential pane instead.
    const yours = compare.yourPanel.filter(
      (r) => matchesFilters(r) && !plannedKeys.has(`give:${r.variant}:${r.id}`),
    );
    const theirs = compare.theirPanel.filter(
      (r) => matchesFilters(r) && !plannedKeys.has(`receive:${r.variant}:${r.id}`),
    );

    // The chosen order first (Dex # or name, either way), then the two
    // priority layers below re-sort STABLY on top of it, so "name A-Z with the
    // wish-listed ones first" is what a trainer gets.
    const order = tradeLeadComparator(leadFilters.sort);
    yours.sort(order);
    theirs.sort(order);

    // Lucky-friend ranking: both-Shiny-and-Lucky → Lucky → Shiny → the rest,
    // legendaries leading each tier. Lives in lib/luckyFriendRanking so the
    // tiering can be tested directly — it had no test, and had quietly lost its
    // Shiny tier entirely. See that module's header.
    if (luckyFriend) {
      yours.sort(luckyLeadComparator(bothShinyAndLuckyIds(yours), isLegendaryDex));
      theirs.sort(luckyLeadComparator(bothShinyAndLuckyIds(theirs), isLegendaryDex));
    }

    // wish-list priority: any species on EITHER trainer's Wish List floats to
    // the very top (a wished copy is the highest-value lead). A stable in-place
    // reorder so it composes with the lucky ranking above and every downstream
    // reference sees the same order.
    if (wishlistedIds.size > 0) {
      const bubble = (rows: PanelRow[]) =>
        rows.sort((a, b) => (wishlistedIds.has(a.id) ? 0 : 1) - (wishlistedIds.has(b.id) ? 0 : 1));
      bubble(yours);
      bubble(theirs);
    }

    return { yourFiltered: yours, theirFiltered: theirs };
  }, [compare, plannedKeys, variantFilter, leadFilters, baseOnly, luckyFriend, wishlistedIds]);

  /**
   * The same lead filters applied to a My Pokémon RECORD (2026-09-29 owner
   * ask: the trade-list block on this tab follows the filter sheet too):
   * the variant pill against the record's own variant, region / search /
   * type / class / costume / regional on its species and form, and Base
   * only. The lucky-friend and reference rules are about the friend's rows
   * and do not apply.
   */
  const leadRecordMatches = useMemo(
    () => (r: MyPokemonRecord) =>
      r.speciesId != null &&
      (variantFilter === "all" || recordCompareVariant(r) === variantFilter) &&
      matchesTradeLead(r.speciesId, leadFilters) &&
      matchesTradeLeadForm(r.speciesId, r.formId ?? null, leadFilters) &&
      (!baseOnly || isBaseStage(r.speciesId) || isBabyEvolution(r.speciesId)),
    [variantFilter, leadFilters, baseOnly],
  );

  // `liveProposals` is rebuilt on every render, so the highlight memo below is
  // keyed on a signature of it rather than on the array identity.
  const liveProposalSig = liveProposals.map((p) => `${p.id}:${p.status}`).join(",");

  /**
   * Trade Match highlight — the single most-suitable swap. Built from the
   * earmarked trade list first (all planned gives × gets), falling back to the
   * first surfaced give + get lead when the list has no give/get yet.
   * Same-variant wins; Lucky is a last resort unless the Lucky-friend toggle is
   * on (see selectTradeMatchHighlight). Share trade and the Trade Match hero
   * both read this.
   *
   * ONCE PROPOSED, A SWAP LEAVES THIS SURFACE. It is now a row in the proposals
   * list below — which is where it can be agreed, declined or cancelled — and
   * the card rolls on to the next-best pairing instead of re-offering a swap
   * that is already asked (see proposedSideKeys for which copies that rules out,
   * and why they are oriented to the reader first). The earmarked rows THEMSELVES
   * stay on the trade list: a declined or cancelled proposal frees its copies
   * again and the pairing comes straight back here.
   *
   * `allProposed` separates "you have earmarked nothing" from "everything you
   * earmarked is already in flight" — two empty cards with nothing in common
   * but their emptiness.
   */
  const { tradeHighlight, tradeMatchAllProposed } = useMemo(() => {
    const spoken = proposedSideKeys(liveProposals, friendId ?? -1);
    const freeGive = (s: TradeSide) => !spoken.gives.has(tradeSideKey(s));
    const freeGet = (s: TradeSide) => !spoken.gets.has(tradeSideKey(s));

    const sides = (direction: TradeDirection): TradeSide[] =>
      trades
        .filter((t) => t.status !== "hidden" && t.direction === direction)
        .map((t) => ({ speciesId: t.speciesId, variant: t.variant }));
    const allGives = sides("give");
    const allGets = sides("receive");
    const plannedGives = allGives.filter(freeGive);
    const plannedGets = allGets.filter(freeGet);

    const asSide = (r: PanelRow): TradeSide => ({ speciesId: r.id, variant: r.variant });
    const fallbackGive = yourFiltered.map(asSide).find(freeGive) ?? null;
    const fallbackGet = theirFiltered.map(asSide).find(freeGet) ?? null;

    const highlight = selectTradeMatchHighlight(
      plannedGives,
      plannedGets,
      fallbackGive,
      fallbackGet,
      luckyFriend,
    );
    return {
      tradeHighlight: highlight,
      tradeMatchAllProposed:
        highlight === null && allGives.length > 0 && allGets.length > 0,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- liveProposalSig stands in for liveProposals
  }, [trades, yourFiltered, theirFiltered, luckyFriend, liveProposalSig, friendId]);

  /* ── actions ────────────────────────────────────────────────────────────── */

  /**
   * Copy a Pokémon GO search that finds the exact copy this trade is about —
   * dex number, variant and `!traded` — rather than the species name, which
   * returned every Vulpix you own when the trade was for a Shiny one. See
   * lib/tradeSearchString.
   */
  function onSearchTrade(t: PlannedTrade) {
    const search = buildTradeSearchString({ speciesId: t.speciesId, variant: t.variant });
    if (!search) return;
    void copyToClipboard(search);
    // Title only. The string is already on the clipboard and about to be
    // pasted — restating it in the toast is a line nobody reads.
    toast({ title: "Copied to clipboard" });
  }

  /*
   * There is no "complete the highlight" action any more. The Trade Match card
   * used to carry one, which finished a swap straight from the card: it wrote
   * the dex lift and deleted the earmarked rows without a proposal ever
   * existing, so the trade left no record either trainer could see. Completion
   * now happens exactly one way — a proposal is agreed (completeProposalMut /
   * applyCompletedTradesToDex above) — and the proposals list is where a swap
   * in flight is cancelled or finished.
   */

  /**
   * Put the swap to the friend IN-APP: create a shared proposal they can agree
   * to, which drops a notification in their bell inbox.
   *
   * Split out of the old one-tap `shareTrade`, which did this AND rendered an
   * image AND opened the native share sheet. One button cannot honestly mean
   * "ask this person" and "export a picture" — and the combined version fired
   * the proposal as a silent side-effect of tapping Share, so a trainer who
   * only wanted the image had also, invisibly, committed to a trade.
   */
  function proposeTrade(h: TradeHighlight | null) {
    if (!h || friendId == null) return;
    apiRequest("POST", `/api/friends/${friendId}/trade-proposals`, {
      giveSpeciesId: h.give.speciesId,
      giveVariant: h.give.variant,
      getSpeciesId: h.get.speciesId,
      getVariant: h.get.variant,
      giveName: speciesName(h.give.speciesId),
      getName: speciesName(h.get.speciesId),
    })
      .then(() => {
        refetchProposals();
        toast({
          title: `Asked ${friendName}`,
          description: "They'll see it in their Nexus Dex, agreeing seals the trade.",
        });
      })
      .catch(() =>
        toast({ title: "Couldn't send that trade", description: "Please try again.", variant: "destructive" }),
      );
  }

  /**
   * Render the branded trade card and hand it to the native share sheet, with a
   * text caption and a clipboard fallback so the button still does something
   * when image generation or the sheet is unavailable.
   *
   * Creates no proposal — this is for sending someone a picture. It DOES record
   * the swap as card-shared on this device (see tradeShareLog), which is what
   * puts the card chip on the trade wherever it appears afterwards, including on
   * the proposal row it becomes if you go on to ask in app.
   *
   * The mark is written up front rather than on success: the share sheet never
   * reports back what a person did with the image, and every path below hands
   * the trade to something outside the app.
   */
  async function exportTradeCard(h: TradeHighlight | TradePairing | null) {
    if (!h) return;
    markSharedAsCard(h);
    // ONE LINE, NO LINK. The caption rides alongside the card image, which
    // already names both sides of the swap and carries the nexusdex.ai footer —
    // so spelling the pair out again in text, and pasting the site a second
    // time, only made the message longer than the thing it introduces.
    const text = "I found a potential trade in Nexus Dex, do you want to trade?";

    try {
      const { blob } = await renderShareCard({
        kind: "trade",
        give: { speciesId: h.give.speciesId, name: speciesName(h.give.speciesId), variant: h.give.variant },
        get: { speciesId: h.get.speciesId, name: speciesName(h.get.speciesId), variant: h.get.variant },
        fromName: trainerDisplayName(account),
        toName: friendName,
      });
      const outcome = await shareImageBlob({
        blob,
        fileName: "nexus-dex-trade.png",
        title: "Nexus Dex trade",
        text,
        dialogTitle: "Share this trade",
      });
      if (outcome === "downloaded") {
        toast({ title: "Trade card saved" });
      }
      return;
    } catch {
      /* Fall through to the plain-text share if the card can't be built. */
    }

    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      navigator.share({ text }).catch(() => {});
      return;
    }
    void copyToClipboard(text);
    toast({ title: "Copied to clipboard" });
  }

  return {
    friendId,
    friend,
    friendName,
    friendLoading,
    isPro,
    openPaywall,
    myAccountId: account?.id ?? null,

    // filters
    region, setRegion,
    typeFilter, toggleType,
    classFilter, toggleClass,
    leadSearch, setLeadSearch,
    leadSort, setLeadSort,
    hideCostume, setHideCostume, hideRegional, setHideRegional,
    leadFilters, leadActiveAxes, leadActiveCount, leadFiltersActive,
    resetLeadFilters,
    variantFilter, setVariantFilter,
    baseOnly, setBaseOnly,
    luckyFriend, setLuckyFriend,
    showAllYours, setShowAllYours,
    showAllTheirs, setShowAllTheirs,
    showAllTradeList, setShowAllTradeList,

    // data
    compare,
    compareQ,
    trades,
    tradeListQ,
    liveProposals,
    liveProposalsQ,
    history,
    historyQ,
    friendWishlist,
    friendWishlistQ,
    yourFiltered,
    theirFiltered,
    leadRecordMatches,
    tradeHighlight,
    tradeMatchAllProposed,
    proposalBusyId,

    // mutations + actions
    addTradeMut,
    hideTradeMut,
    removeTradeMut,
    proposalActionMut,
    completeProposalMut,
    onSearchTrade,
    proposeTrade,
    exportTradeCard,
  };
}
